import { uniqBy, has } from 'lodash-es';

import { getPolls, getTags, getMultiplePolls } from '@/js/services/polls';

const state = {
  polls: [],
  allTags: [],
  lookupTable: {},

  // Track which polls are being loaded by ID
  isLoadingMap: {},

  isLoading: false,
  page: 1,
  limit: 10,
  total: 0,
  tags: [],
  search: '',
};

const actions = {
  async getPolls(context) {
    const { tags, page, search } = context.state;

    context.commit('SET_FIELD', { key: 'isLoading', value: true });

    const opts = { page, search };

    if (tags.length) {
      opts.tags = tags.join(',');
    }

    const { polls, total } = await getPolls(opts);

    context.commit('SET_FIELD', { key: 'isLoading', value: false });
    context.commit('SET_POLLS', polls);
    context.commit('SET_FIELD', { key: 'total', value: total });
  },

  async getPollTags({ commit }) {
    const { tags } = await getTags();
    const lowercaseTags = tags.map((tag) => tag.toLowerCase());
    const uniqueTags = Array.from(new Set(lowercaseTags));
    commit('SET_FIELD', { key: 'allTags', value: uniqueTags });
  },

   /**
   * Increment page number and fetch some more themes
   */
  incrementPollsPage(context) {
    context.commit('SET_FIELD', { key: 'page', value: context.state.page + 1 });
    context.dispatch('getPolls');
  },

  setPolls(context, payload) {
    context.commit('SET_POLLS', payload);
  },

  /**
   * Clear current themes array and reset pagination
   */
  resetPolls(context) {
    context.commit('RESET_POLLS');
  },

  changePollsTags({ commit, dispatch }, tags) {
   commit('SET_FIELD', { key: 'tags', value: tags });
   dispatch('resetPolls');
   dispatch('getPolls');
  },

  changePollsSearch({ commit, dispatch }, search) {
   commit('SET_FIELD', { key: 'search', value: search });
   dispatch('resetPolls');
   dispatch('getPolls');
  },

  /**
   * Clear any tags used to filter results, reset page to 1, then refetch Polls
   */
  resetPollsSearch(context) {
    context.commit('CLEAR_POLL_TAGS');
    context.commit('SET_FIELD', { key: 'search', value: '' });
    context.dispatch('resetPolls');
    context.dispatch('getPolls');
  },

  async getTimelinePolls({ commit, rootState }) {
      const { timeline } = rootState.production.production;
  
      let pollIds = timeline.reduce((memo, item) => {
        if (item.layout === 'poll') {
          memo.add(item.pollId);
        }
  
        return memo;
      }, new Set());

      pollIds = [...pollIds].filter((id) => !has(state.lookupTable, id));
      if (!pollIds.length) {
        return;
      }
  
      commit('SET_LOADING', { ids: pollIds, loading: true });

      const { polls } = await getMultiplePolls(pollIds).catch((err) => {
        console.error('Failed to fetch multiple polls', err);
        return { polls: [] };
      });

      commit('ADD_TO_LOOKUP_TABLE', polls);
      commit('SET_LOADING', { ids: pollIds, loading: false });
    },
};

const mutations = {
  /* eslint-disable no-param-reassign, no-shadow */

  SET_FIELD(state, { key, value }) {
    state[key] = value;
  },

  /**
   * Set loading flags for specific polls by ID,
   * so we know in the poll editor if a poll is still loading.
   */
  SET_LOADING(state, { ids, loading }) {
    state.isLoadingMap = {
      ...state.isLoadingMap,
      ...ids.reduce((memo, id) => ({
        ...memo,
        [id]: loading,
      }), {}),
    };
  },

  SET_POLLS(state, polls) {
    state.polls = uniqBy(state.polls.concat(polls), 'pollId');
  },

  ADD_POLL_TAG(state, tag) {
    state.tags = [...state.tags, tag];
  },

  REMOVE_POLL_TAG(state, tag) {
    state.tags = state.tags.filter((t) => t !== tag);
  },

  CLEAR_POLL_TAGS(state) {
    state.tags = [];
  },

  RESET_POLLS(state) {
    state.polls = [];
    state.page = 1;
    state.total = 0;
  },

  ADD_TO_LOOKUP_TABLE(state, polls) {
    state.lookupTable = {
      ...state.lookupTable,
      ...(polls.reduce((memo, poll) => ({
        ...memo,
        [poll.pollId]: {
          ...state.lookupTable[poll.pollId],
          ...poll,
        },
      }), {})),
    };
  },
};

const getters = {
  hasMorePolls(state) {
    return state.total > (state.page * state.limit);
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
